import { useForm } from '@inertiajs/react';
import {
    AlertCircle,
    ExternalLink,
    Paperclip,
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Breadcrumbs from '@/components/Breadcrumbs';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import FileUploader from '@/components/FileUploader';
import TextInput from '@/components/Forms/TextInput';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import AppLayout from '@/layouts/AppLayout';
import {
    AssignmentDetail as AssignmentDetailType,
    AssignmentSubmission,
} from '@/types';
import { cleanWordPressHtml } from '@/utils/cleanHtml';

interface AssignmentDetailProps {
    assignment: AssignmentDetailType;
    submissions: AssignmentSubmission[];
    can_submit: boolean;
    latest_submission_status: 'pending' | 'approved' | 'rejected' | null;
    form_data: Record<string, string>;
    user_full_name: string;
    has_discord_username: boolean;
}

const statusStyles: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-rose-100 text-rose-700',
};

const AssignmentDetailContent: React.FC<AssignmentDetailProps> = ({
    assignment,
    submissions,
    can_submit,
    latest_submission_status,
    form_data,
    user_full_name,
    has_discord_username,
}) => {
    const { t } = useTranslation();
    const { route } = useRoutes();

    const { data, setData, post, processing, errors } = useForm({
        discord_username: form_data.discord_username || '',
        url_1: form_data.url_1 || '',
        url_2: form_data.url_2 || '',
        evidence: null as File | null,
    });

    const formatDate = (value: string | null) => {
        if (!value) {
            return t('assignment.history.notReviewed');
        }
        return new Date(value).toLocaleString();
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        post(`/assignments/${assignment.slug}/submit/`, {
            forceFormData: true,
            preserveScroll: true,
            onSuccess: () => {
                setData('evidence', null);
            },
        });
    };

    const getStatusLabel = (status: string) => {
        return t(`assignment.history.status.${status}` as const);
    };

    const getStatusClass = (status: string) => {
        return statusStyles[status] || 'bg-gray-100 text-gray-700';
    };

    const pendingMessage = () => {
        if (!can_submit) {
            if (latest_submission_status === 'pending') {
                return t('assignment.submitSection.pending');
            }
            if (latest_submission_status === 'approved') {
                return t('assignment.submitSection.approved');
            }
            return t('assignment.submitSection.inactive');
        }
        return null;
    };

    const breadcrumbItems = [
        {
            label: t('course.sectionDetail.courses'),
            href: route('home').toString(),
        },
        {
            label: assignment.course.title,
            href: route('course-detail', {
                slug: assignment.course.slug,
            }).toString(),
        },
        {
            label: assignment.section.title,
            href: route('section-detail', {
                slug: assignment.course.slug,
                section_id: assignment.section.id,
            }).toString(),
        },
        { label: assignment.title },
    ];

    return (
        <AppLayout>
            <PageTitle title={assignment.title} />
            <div className="min-h-screen bg-gray-50">
                <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
                    <div className="mb-6">
                        <Breadcrumbs items={breadcrumbItems} />
                    </div>

                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                        <div className="space-y-6 lg:col-span-2">
                            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                                <div className="mb-6">
                                    <p className="text-sm font-semibold tracking-wide text-emerald-600 uppercase">
                                        {t('assignment.pageTitle')}
                                    </p>
                                    <h1 className="mt-2 text-3xl font-bold text-gray-900">
                                        {assignment.title}
                                    </h1>
                                </div>

                                {assignment.description ? (
                                    <div className="html-content max-w-none text-gray-700">
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: cleanWordPressHtml(
                                                    assignment.description
                                                ),
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <p className="text-gray-600">
                                        {t('assignment.noDescription')}
                                    </p>
                                )}

                                <div className="mt-8">
                                    <h2 className="text-lg font-semibold text-gray-900">
                                        {t('assignment.attachments')}
                                    </h2>
                                    {assignment.attachments.length === 0 ? (
                                        <p className="mt-2 text-sm text-gray-500">
                                            {t('assignment.noAttachments')}
                                        </p>
                                    ) : (
                                        <ul className="mt-4 space-y-3">
                                            {assignment.attachments.map(
                                                (attachment) => (
                                                    <li
                                                        key={attachment.id}
                                                        className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 text-sm hover:border-emerald-400"
                                                    >
                                                        <div className="flex items-center space-x-3">
                                                            <Paperclip className="h-4 w-4 text-emerald-500" />
                                                            <span className="font-medium text-gray-700">
                                                                {
                                                                    attachment.filename
                                                                }
                                                            </span>
                                                        </div>
                                                        <a
                                                            href={
                                                                attachment.url
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center space-x-1 text-sm font-medium text-emerald-600 hover:text-emerald-700"
                                                        >
                                                            <span>
                                                                {t(
                                                                    'assignment.download'
                                                                )}
                                                            </span>
                                                            <ExternalLink className="h-4 w-4" />
                                                        </a>
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                                <div className="mb-6 flex items-center justify-between">
                                    <h2 className="text-lg font-semibold text-gray-900">
                                        {t('assignment.history.title')}
                                    </h2>
                                    <span className="text-sm text-gray-500">
                                        {t('assignment.history.total', {
                                            count: submissions.length,
                                        })}
                                    </span>
                                </div>

                                {submissions.length === 0 ? (
                                    <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center">
                                        <p className="text-sm text-gray-500">
                                            {t('assignment.history.empty')}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {submissions.map((submission) => (
                                            <div
                                                key={submission.id}
                                                className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                                            >
                                                <div className="flex flex-wrap items-center justify-between gap-3">
                                                    <span
                                                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold tracking-wide uppercase ${getStatusClass(submission.status)}`}
                                                    >
                                                        {getStatusLabel(
                                                            submission.status
                                                        )}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {t(
                                                            'assignment.history.submittedAt',
                                                            {
                                                                date: formatDate(
                                                                    submission.created_at
                                                                ),
                                                            }
                                                        )}
                                                    </span>
                                                </div>

                                                <div className="mt-4 space-y-2 text-sm text-gray-600">
                                                    {submission.url_1 && assignment.url_1_label ? (
                                                        <div className="flex items-center space-x-2">
                                                            <ExternalLink className="h-4 w-4 text-emerald-500" />
                                                            <a
                                                                href={submission.url_1}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="font-medium text-emerald-600 hover:text-emerald-700"
                                                            >
                                                                {assignment.url_1_label}
                                                            </a>
                                                        </div>
                                                    ) : null}
                                                    {submission.url_2 && assignment.url_2_label ? (
                                                        <div className="flex items-center space-x-2">
                                                            <ExternalLink className="h-4 w-4 text-emerald-500" />
                                                            <a
                                                                href={submission.url_2}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="font-medium text-emerald-600 hover:text-emerald-700"
                                                            >
                                                                {assignment.url_2_label}
                                                            </a>
                                                        </div>
                                                    ) : null}
                                                    {submission.evidence_url && (
                                                        <div className="flex items-center space-x-2">
                                                            <Paperclip className="h-4 w-4 text-emerald-500" />
                                                            <a
                                                                href={
                                                                    submission.evidence_url
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="font-medium text-emerald-600 hover:text-emerald-700"
                                                            >
                                                                {t(
                                                                    'assignment.history.evidence'
                                                                )}
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="mt-4 grid gap-3 text-sm text-gray-600 md:grid-cols-2">
                                                    <div>
                                                        <p className="font-medium text-gray-700">
                                                            {t(
                                                                'assignment.history.reviewedAt',
                                                                {
                                                                    date: formatDate(
                                                                        submission.reviewed_at
                                                                    ),
                                                                }
                                                            )}
                                                        </p>
                                                        {submission.reviewed_by
                                                            ?.name && (
                                                            <p className="text-xs text-gray-500">
                                                                {t(
                                                                    'assignment.history.reviewedBy',
                                                                    {
                                                                        name: submission
                                                                            .reviewed_by
                                                                            .name,
                                                                    }
                                                                )}
                                                            </p>
                                                        )}
                                                    </div>
                                                    {submission.teacher_comment && (
                                                        <div>
                                                            <p className="font-medium text-gray-700">
                                                                {t(
                                                                    'assignment.history.teacherComment'
                                                                )}
                                                            </p>
                                                            <p className="text-xs text-gray-600">
                                                                {
                                                                    submission.teacher_comment
                                                                }
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="lg:col-span-1">
                            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                                <h2 className="text-lg font-semibold text-gray-900">
                                    {t('assignment.submitSection.title')}
                                </h2>
                                <p className="mt-1 text-sm text-gray-500">
                                    {t('assignment.submitSection.subtitle')}
                                </p>

                                {pendingMessage() ? (
                                    <div className="mt-6 flex items-start space-x-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                                        <AlertCircle className="h-5 w-5 flex-shrink-0" />
                                        <span>{pendingMessage()}</span>
                                    </div>
                                ) : null}

                                <form
                                    onSubmit={handleSubmit}
                                    className="mt-6 space-y-4"
                                    encType="multipart/form-data"
                                >
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                                        <p className="text-sm font-medium text-gray-700">
                                            {t('assignment.form.submittingAs')}
                                        </p>
                                        <p className="mt-1 text-base font-semibold text-gray-900">
                                            {user_full_name}
                                        </p>
                                    </div>
                                    {!has_discord_username ? (
                                        <TextInput
                                            label={t(
                                                'assignment.form.discordUsername'
                                            )}
                                            name="discord_username"
                                            value={data.discord_username}
                                            onChange={(value) =>
                                                setData(
                                                    'discord_username',
                                                    value
                                                )
                                            }
                                            error={errors.discord_username}
                                            required
                                        />
                                    ) : null}
                                    {assignment.url_1_label ? (
                                        <TextInput
                                            label={assignment.url_1_label}
                                            name="url_1"
                                            type="url"
                                            value={data.url_1}
                                            onChange={(value) =>
                                                setData('url_1', value)
                                            }
                                            error={errors.url_1}
                                            required={assignment.url_1_mandatory}
                                        />
                                    ) : null}
                                    {assignment.url_2_label ? (
                                        <TextInput
                                            label={assignment.url_2_label}
                                            name="url_2"
                                            type="url"
                                            value={data.url_2}
                                            onChange={(value) =>
                                                setData('url_2', value)
                                            }
                                            error={errors.url_2}
                                            required={assignment.url_2_mandatory}
                                        />
                                    ) : null}
                                    <div>
                                        <p className="mb-2 text-sm font-medium text-gray-700">
                                            {assignment.evidence_label}{assignment.evidence_mandatory ? ' *' : ''}
                                        </p>
                                        <FileUploader
                                            onFileSelect={(file) =>
                                                setData('evidence', file)
                                            }
                                            selectedFile={data.evidence}
                                            accept=""
                                        />
                                        {errors.evidence && (
                                            <p className="mt-1 text-xs text-rose-600">
                                                {errors.evidence}
                                            </p>
                                        )}
                                    </div>

                                    <PrimaryButton
                                        type="submit"
                                        className="w-full justify-center"
                                        disabled={!can_submit || processing}
                                        loading={processing}
                                    >
                                        {processing
                                            ? t('assignment.form.submitting')
                                            : t('assignment.form.submit')}
                                    </PrimaryButton>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

const AssignmentDetail: React.FC<AssignmentDetailProps> = (props) => {
    return (
        <ThemeWrapper>
            <AssignmentDetailContent {...props} />
        </ThemeWrapper>
    );
};

export default AssignmentDetail;
